import { newProjectId } from '@/pages/AdvancedEditor/types';
import { useIdb } from '@/services/idb';
import { LibraryItem, LibraryItemStatus, LibraryItemType, ScenePresenter } from '@d-id/types';
import { useCallback } from 'react';
import { useQuery } from 'react-query';
import { getPageProperty, PolotnoStoreActivePageKeys } from '../usePolotnoActivePageState';
import { getStoreProperty, PolotnoStoreKeys } from '../usePolotnoState';
import { ProjectScript } from './useGetPolotnoInitialData';

interface IdbProjectDraft {
    createdAt: string;
    store: any;
    videoName?: string;
    presenters?: (ScenePresenter | undefined)[];
    id?: string;
    scripts?: (ProjectScript | {} | null)[];
}

export const PROJECT_IDB_DRAFT_QUERY_KEY = 'idbDraft';

export const useProjectIdb = () => {
    const idb = useIdb();
    const getIdbDraft = useCallback(async () => {
        const draft = await idb?.get('v2drafts', newProjectId);

        if (!draft?.store.pages.length) return null;

        return draft;
    }, [idb]);

    const clearIdbDraft = useCallback(async () => {
        await idb?.delete('v2drafts', newProjectId);
    }, [idb]);

    const saveIdbDraft = useCallback(
        async (data: IdbProjectDraft) => {
            await idb?.put('v2drafts', data);
        },
        [idb]
    );

    const idbDraft = useQuery(PROJECT_IDB_DRAFT_QUERY_KEY, getIdbDraft);

    return { getIdbDraft, clearIdbDraft, saveIdbDraft, idbDraft };
};

export const parseIdbDraftIntoLibraryItem = (draft: IdbProjectDraft): LibraryItem | undefined => {
    if (!draft?.store?.pages?.length) return;

    return {
        id: newProjectId,
        type: LibraryItemType.PROJECT,
        status: LibraryItemStatus.Created,
        created_at: draft.createdAt || new Date().toISOString(),
        modified_at: draft.createdAt || new Date().toISOString(),
        thumbnail_url: getPageProperty(draft.store.pages[0], PolotnoStoreActivePageKeys.thumbnail),
        name: getStoreProperty(draft.store, PolotnoStoreKeys.creativeVideoName),
    };
};
